/* eslint-disable no-empty-function */
/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Badge,
  Center,
  HStack,
  Skeleton,
  useBreakpointValue,
} from '@wegroup/design-system';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { showFranchise } from '../../../../../tarifications/src/legacy/services/proFlowService';
import { isOfferFailed, LouiseOffer } from '../../../../../../types/Quotes';
import InsuranceCompanyLogo from '../../../../../common/src/components/InsuranceCompanyLogo';
import { bp, mq } from '../../../../../common/src/utils/styledComponentsUtils';
import CollapseContainer from '../../CollapseContainer';
import { RootState } from '../../../store/redux/reducers/rootReducer';
import { OfferState } from '../../../services/offersService';
import {
  getEuroValue,
  getFieldValue,
  getGuaranteeNameForKey,
} from '../../../services/productFactoryService';
import { toEuro } from '../../../utils/stringUtils';
import { setRequiredInformation } from '../../../store/redux/slices/offersSlice';
import ComparisonMatrixOfferHeader from '../../../features/comparison/components/comparison-matrix/mobile/header/ComparisonMatrixOfferHeader';
import LabelValue from '../../offers/LabelValue';
import LabelValueWithSkeleton from '../../offers/LabelValueWithSkeleton';
import SingleSelectedOfferPrice from '../selected-offer-summary/SingleSelectedOfferPrice';
import SingleSelectedOfferOptions from './SingleSelectedOfferOptions';
import SingleSelectedOffersRequirements from './SingleSelectedOffersRequirements';
import { isSocialLoginUrl } from '../../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  offer: LouiseOffer;
  riskObject: string;
}

const SingleSelectedOfferInfo: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
  riskObject,
}) => {
  const { t } = useTranslation();
  const { offersInfo } = useSelector((state: RootState) => state.offers);
  const dispatch = useDispatch();
  const isMobile = useBreakpointValue({ base: true, laptop: false });

  const { watch, setValue } = useForm({
    defaultValues: {
      reasonOfContact: offersInfo.reasonOfContact[offer.hash_id] || '',
      requiredInformation: offersInfo.requiredInformation[offer.hash_id] || '',
    },
  });

  return (
    <Info>
      {isOfferFailed(offer) && (
        <Badge position="absolute" top="2" left="2" variant="error">
          {t('Failed')}
        </Badge>
      )}
      <StyledComparisonMatrixOfferHeader
        Icon={
          <InsuranceCompanyLogo insuranceCompany={offer.info.company.key} />
        }
        title={`${
          offer.info.company.name || t('Insurer')
        }  ${getGuaranteeNameForKey(
          offer.info.insurance_name,
          offer?.guarantee_content,
        )}`}
        description={
          offer.state === OfferState.LOADING ? (
            <Skeleton h="4" w="40" />
          ) : (
            <HStack>
              <SingleSelectedOfferPrice
                price={toEuro(offer.premium?.total_premium || 0)}
                interval={t(
                  offer.details?.payment_interval?.toUpperCase() || '',
                )}
              />
            </HStack>
          )
        }
      />
      <InfoHead>
        <Center
          display={{ base: 'none', laptop: 'block' }}
          mr="4"
          maxH="10"
          width="32"
        >
          <InsuranceCompanyLogo insuranceCompany={offer.info.company.key} />
        </Center>
        <LabelValue label={offer.info.company?.name || t('Insurer')}>
          {getGuaranteeNameForKey(
            offer.info.insurance_name,
            offer?.guarantee_content,
          )}
        </LabelValue>
      </InfoHead>
      {{
        home: (
          <StyledCollapseContainer title={t('Info')} show={isMobile}>
            <Values>
              <LabelValueWithSkeleton
                label={t(
                  'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_INFO_HOME_PROPERTY-VALUE',
                )}
              >
                {getEuroValue(offer.state, offer.premium?.insured_value?.value)}
              </LabelValueWithSkeleton>
              <LabelValueWithSkeleton
                label={t(
                  'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_INFO_HOME_CONTENT-VALUE',
                )}
              >
                {getEuroValue(
                  offer.state,
                  offer.premium?.insured_content_value?.value,
                )}
              </LabelValueWithSkeleton>
            </Values>
          </StyledCollapseContainer>
        ),
        car: (
          <StyledCollapseContainer title={t('Info')} show={isMobile}>
            <Values>
              <LabelValueWithSkeleton label={t('Bonus malus')}>
                {getFieldValue(offer.state, offer.details?.bonus_malus)}
              </LabelValueWithSkeleton>
              <LabelValueWithSkeleton label={t('Franchise')}>
                {getFieldValue(
                  offer.state,
                  showFranchise(
                    offer.premium?.franchise?.type,
                    offer.premium?.franchise?.value,
                  ),
                )}
              </LabelValueWithSkeleton>
            </Values>
          </StyledCollapseContainer>
        ),
      }[riskObject] || ''}

      <StyledCollapseContainer title={t('Options')} show={isMobile}>
        <SingleSelectedOfferOptions
          offer={offer}
          isMobile={isMobile}
          isStatic
        />
      </StyledCollapseContainer>
      {!isSocialLoginUrl && (
        <StyledCollapseContainer
          title={t('Information Requirements')}
          show={isMobile}
        >
          <SingleSelectedOffersRequirements
            value={watch('requiredInformation')}
            placeholder={t(
              'Justify the advice you have given to the end customer',
            )}
            isMobile={isMobile}
            onChange={(text: string) => setValue('requiredInformation', text)}
            onBlur={() =>
              dispatch(
                setRequiredInformation({
                  quoteId: offer.hash_id,
                  text: watch('requiredInformation'),
                }),
              )
            }
          />
        </StyledCollapseContainer>
      )}
    </Info>
  );
};

const StyledCollapseContainer = styled(CollapseContainer)`
  /** Max width media query required here */
  @media screen and (max-width: ${bp.laptop}px) {
    > div:first-child {
      min-height: unset;
      padding: 0;
    }

    > div:last-child {
      min-height: unset;
      padding-top: 1.6rem;
      padding-bottom: 0;
    }
  }
`;

const StyledComparisonMatrixOfferHeader = styled(ComparisonMatrixOfferHeader)`
  padding: 1.6rem;
  ${mq.laptop} {
    display: none;
  }
`;

const InfoHead = styled.div`
  width: 100%;
  display: none;

  ${mq.laptop} {
    display: flex;
  }
`;

const Info = styled.div`
  position: relative;
  flex-grow: 1;

  > div {
    padding: 1.6rem;
    border-bottom: 1px solid ${({ theme }) => theme.gray[50]};

    ${mq.laptop} {
      padding: 2.4rem 0;

      &:last-of-type {
        padding-bottom: 0;
        border-bottom: unset;
        margin-bottom: 0;
      }
    }
  }

  ${mq.laptop} {
    padding: 2.4rem;
  }
`;

const Values = styled.div`
  display: flex;
  > div {
    margin-right: 3.2rem;
  }
`;

export default SingleSelectedOfferInfo;
